import { useState } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { Menu, Shield, X } from 'lucide-react'
import { Brand } from '@/components/Brand'
import { ThemeToggle } from '@/components/ThemeToggle'
import { useAuth } from '@/lib/auth'
import { useIsAdmin } from '@/lib/admin'
import { pickCoordParams } from '@/lib/coord-url'

interface NavItem {
  label: string
  href: string
  /** Returns true if the current location should highlight this entry. */
  matches?: (pathname: string) => boolean
  /** Carry the current lat/lon/zoom params (the two map pages: home + Explore). */
  carryCoords?: boolean
}

const NAV: ReadonlyArray<NavItem> = [
  { label: 'Explore', href: '/explore', matches: (p) => p.startsWith('/explore'), carryCoords: true },
  { label: 'API',     href: '/api',     matches: (p) => p.startsWith('/api') },
  { label: 'Docs',    href: '/docs',    matches: (p) => p.startsWith('/docs') },
  { label: 'Data',    href: '/data',    matches: (p) => p.startsWith('/data') },
]

// NOTE: no display utility here on purpose. Tailwind's generated CSS
// emits `.inline-flex` after `.hidden`, so adding `inline-flex` to the
// base would override any `hidden md:inline-flex` a caller layered on
// top — the auth buttons would stay visible on mobile despite the
// responsive-hide intent. Every consumer adds its own display utility
// (`hidden md:inline-flex`) below so it's the only display rule
// applied and the cascade works.
const BUTTON_BASE = [
  'items-center px-3.5 py-2 rounded-[10px]',
  'text-sm font-medium cursor-pointer',
  'transition-colors',
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-accent)] focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--color-surface)]',
].join(' ')

/**
 * `minimal` renders just the brand + theme toggle — no primary nav, no auth
 * buttons, no mobile drawer. Used by focused flows (e.g. /reset-password)
 * where the surrounding menu would be a distraction or an escape hatch out
 * of the task, while keeping the normal header chrome.
 */
export function SiteHeader({ minimal = false }: { minimal?: boolean } = {}) {
  const { pathname, search } = useLocation()
  // Carry the current map view (lat/lon/zoom) onto the logo + Explore links so
  // switching pages keeps the same location + zoom. Empty on non-map pages.
  const coordSearch = pickCoordParams(search)
  const navigate = useNavigate()
  const { user, signOut, loading } = useAuth()
  const { isAdmin, loading: adminLoading } = useIsAdmin()
  const signedIn = Boolean(user)
  const showAdminLink = signedIn && !adminLoading && isAdmin
  const [mobileOpen, setMobileOpen] = useState(false)

  if (minimal) {
    return (
      <header className="w-full border-b border-[var(--color-border)] bg-[var(--color-surface)]">
        <div className="mx-auto flex max-w-7xl items-center justify-between gap-4 px-4 py-3 sm:px-6">
          <Link
            to={{ pathname: '/', search: coordSearch }}
            className="rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-accent)] focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--color-surface)]"
          >
            <Brand />
          </Link>
          <ThemeToggle />
        </div>
      </header>
    )
  }

  async function onSignOut() {
    await signOut()
    // Route to /sign-in with a "you're logged out" indicator. Keeps a
    // visible confirmation that the action succeeded — landing on the
    // public homepage with no feedback was indistinguishable from
    // "nothing happened". Same destination from AccountLayout's
    // Sign-out so the post-logout experience is consistent.
    navigate('/sign-in?logged-out=1', { replace: true })
  }

  return (
    <header className="w-full border-b border-[var(--color-border)] bg-[var(--color-surface)]">
      <div className="mx-auto flex max-w-7xl items-center justify-between gap-4 px-4 py-3 sm:px-6">
        <Link
          to={{ pathname: '/', search: coordSearch }}
          className="rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-accent)] focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--color-surface)]"
        >
          <Brand />
        </Link>

        <nav aria-label="Primary" className="hidden md:flex items-center gap-1">
          {NAV.map((item) => {
            const active = item.matches?.(pathname) ?? false
            return (
              <Link
                key={item.label}
                to={item.carryCoords ? { pathname: item.href, search: coordSearch } : item.href}
                className={[
                  'inline-flex flex-col items-center px-2.5 py-1.5 text-sm rounded-md',
                  'transition-colors',
                  active
                    ? 'text-[var(--color-text)]'
                    : 'text-[var(--color-muted)] hover:text-[var(--color-text)]',
                ].join(' ')}
              >
                <span>{item.label}</span>
                <span
                  aria-hidden
                  className={[
                    'mt-1 h-px w-full rounded-full transition-colors',
                    active ? 'bg-[var(--color-accent)]' : 'bg-transparent',
                  ].join(' ')}
                />
              </Link>
            )
          })}
        </nav>

        <div className="flex items-center gap-1.5">
          <ThemeToggle />
          {/* Mobile hamburger — visible only below md. Toggles the drawer
              that mirrors the desktop NAV (Explore + API). */}
          <button
            type="button"
            aria-label={mobileOpen ? 'Close menu' : 'Open menu'}
            aria-expanded={mobileOpen}
            onClick={() => setMobileOpen((v) => !v)}
            className="md:hidden inline-flex items-center justify-center w-9 h-9 rounded-md text-[var(--color-text)] hover:bg-[var(--color-elev)] transition-colors"
          >
            {mobileOpen ? <X size={18} aria-hidden /> : <Menu size={18} aria-hidden />}
          </button>
          {loading ? (
            // Reserve space so the header doesn't shift once auth bootstraps.
            <span aria-hidden className="ml-1 h-9 w-[124px]" />
          ) : signedIn ? (
            // Below md these buttons are hidden — auth actions live in
            // the hamburger drawer so the mobile top bar is just
            // logo / theme-toggle / hamburger.
            <>
              {showAdminLink && (
                <Link
                  to="/admin"
                  className={[
                    BUTTON_BASE,
                    'hidden md:inline-flex',
                    'ml-1 gap-1.5',
                    'text-[var(--color-accent)] border border-[var(--color-accent)]/40',
                    'hover:border-[var(--color-accent)] hover:bg-[var(--color-accent-soft)]',
                  ].join(' ')}
                >
                  <Shield size={14} aria-hidden />
                  Admin
                </Link>
              )}
              <Link
                to="/account"
                className={[
                  BUTTON_BASE,
                  'hidden md:inline-flex',
                  'ml-1',
                  'text-[var(--color-text)] border border-[var(--color-border)]',
                  'hover:border-[var(--color-accent)]/60 hover:text-[var(--color-accent)]',
                ].join(' ')}
              >
                Account
              </Link>
              <button
                type="button"
                onClick={onSignOut}
                className={[
                  BUTTON_BASE,
                  'hidden md:inline-flex',
                  'text-[var(--color-muted)] border border-transparent',
                  'hover:text-[var(--color-text)] cursor-pointer',
                ].join(' ')}
              >
                Sign Out
              </button>
            </>
          ) : (
            <>
              <Link
                to="/sign-in"
                className={[
                  BUTTON_BASE,
                  'hidden md:inline-flex',
                  'ml-1',
                  'text-[var(--color-text)] border border-[var(--color-border)]',
                  'hover:border-[var(--color-accent)]/60 hover:text-[var(--color-accent)]',
                ].join(' ')}
              >
                Sign In
              </Link>
              <Link
                to="/sign-up"
                className={[
                  BUTTON_BASE,
                  'hidden md:inline-flex',
                  'bg-[var(--color-accent)] text-[var(--color-surface)]',
                  'hover:bg-[var(--color-accent-hover)]',
                ].join(' ')}
              >
                Get API Key
              </Link>
            </>
          )}
        </div>
      </div>

      {/* Mobile drawer — slides down below the header. Hidden on md+ since
          the desktop nav already shows the same links. Carries BOTH the
          NAV items (Explore / API / Docs) AND the auth actions
          (Sign In + Get API Key, or Admin / Account / Sign Out) since
          the auth buttons are hidden in the mobile top bar. */}
      {mobileOpen && (
        <nav
          aria-label="Primary"
          className="md:hidden border-t border-[var(--color-border)] bg-[var(--color-surface)]"
        >
          <ul className="mx-auto max-w-7xl px-4 py-2 sm:px-6">
            {NAV.map((item) => {
              const active = item.matches?.(pathname) ?? false
              return (
                <li key={item.label}>
                  <Link
                    to={item.carryCoords ? { pathname: item.href, search: coordSearch } : item.href}
                    onClick={() => setMobileOpen(false)}
                    className={[
                      'flex items-center py-2 text-[15px] rounded-md px-1',
                      active
                        ? 'text-[var(--color-accent)] font-semibold'
                        : 'text-[var(--color-text)] hover:text-[var(--color-accent)]',
                    ].join(' ')}
                  >
                    {item.label}
                  </Link>
                </li>
              )
            })}
            {/* Visual separator between site nav and auth actions. */}
            <li aria-hidden className="my-2 border-t border-[var(--color-border)]" />
            {loading ? null : signedIn ? (
              <>
                {showAdminLink ? (
                  <li>
                    <Link
                      to="/admin"
                      onClick={() => setMobileOpen(false)}
                      className="flex items-center gap-2 py-2 px-1 text-[15px] rounded-md text-[var(--color-accent)] hover:bg-[var(--color-accent-soft)]"
                    >
                      <Shield size={15} aria-hidden />
                      Admin
                    </Link>
                  </li>
                ) : null}
                <li>
                  <Link
                    to="/account"
                    onClick={() => setMobileOpen(false)}
                    className="flex items-center py-2 px-1 text-[15px] rounded-md text-[var(--color-text)] hover:text-[var(--color-accent)]"
                  >
                    Account
                  </Link>
                </li>
                <li>
                  <button
                    type="button"
                    onClick={() => {
                      setMobileOpen(false)
                      void onSignOut()
                    }}
                    className="w-full text-left flex items-center py-2 px-1 text-[15px] rounded-md text-[var(--color-muted)] hover:text-[var(--color-signal)] cursor-pointer"
                  >
                    Sign Out
                  </button>
                </li>
              </>
            ) : (
              <>
                <li>
                  <Link
                    to="/sign-in"
                    onClick={() => setMobileOpen(false)}
                    className="flex items-center py-2 px-1 text-[15px] rounded-md text-[var(--color-text)] hover:text-[var(--color-accent)]"
                  >
                    Sign In
                  </Link>
                </li>
                <li>
                  <Link
                    to="/sign-up"
                    onClick={() => setMobileOpen(false)}
                    className="mt-1 flex items-center justify-center py-2 px-3 text-[14px] font-medium rounded-md bg-[var(--color-accent)] text-[var(--color-surface)] hover:bg-[var(--color-accent-hover)]"
                  >
                    Get API Key
                  </Link>
                </li>
              </>
            )}
          </ul>
        </nav>
      )}
    </header>
  )
}
